import { useCallback, useEffect, useMemo } from 'react';
import { useScope } from '@workspace/scope-context';

import { useTour, type TourDefinition, type TourMatchContext } from '@workspace/tour';
import { useSession } from '@/context/SessionContext';

const portalTours: TourDefinition[] = [
  {
    id: 'portal.dashboard.overview',
    app: 'app-portal',
    version: 4,
    trigger: { path: /^\/(?:dashboard)?$/ },
    autoStart: false,
    steps: [
      {
        id: 'brand',
        title: 'Your workspace brand',
        description: 'The header carries tenant branding so you always know which portal environment you are managing.',
        target: { anchorId: 'portal.brand' },
        placement: 'right'
      },
      {
        id: 'workspace',
        title: 'Portal home',
        description: 'Every route renders inside this tenant-scoped workspace. Start here, then move into the feature area you need.',
        target: { anchorId: 'portal.workspace' },
        placement: 'top',
        padding: 16
      },
      {
        id: 'quick-actions',
        title: 'Jump to common tasks',
        description: 'Quick actions open support, help, and setup shortcuts so you can move without backing out of the current page.',
        target: { anchorId: 'portal.quickActions' },
        placement: 'bottom'
      },
      {
        id: 'command-palette',
        title: 'Search and navigate fast',
        description: 'Press ⌘K (Mac) or Ctrl+K (Windows/Linux) to open the command bar. Search pages or actions without clicking through menus.',
        target: { anchorId: 'portal.command' },
        placement: 'bottom'
      }
    ]
  }
];

export function usePortalTours() {
  const { registerTours } = useTour();

  useEffect(() => {
    const unregister = registerTours(portalTours);
    return unregister;
  }, [registerTours]);
}

function matchesTrigger(trigger: TourDefinition['trigger'], path: string) {
  if (!trigger?.path) {
    return false;
  }
  if (typeof trigger.path === 'string') {
    return trigger.path === path;
  }
  return trigger.path.test(path);
}

export function usePortalManualTour(path: string) {
  const { session } = useSession();
  const scope = useScope();
  const { startTour, isReady } = useTour();

  const matchedTour = useMemo(() => {
    const context: TourMatchContext = { path, session, scope };
    for (const tour of portalTours) {
      if (!matchesTrigger(tour.trigger, path)) {
        continue;
      }
      if (tour.guard && !tour.guard(context)) {
        continue;
      }
      return tour;
    }
    return null;
  }, [path, scope, session]);

  const canStart = Boolean(isReady && matchedTour);
  const start = useCallback(() => {
    if (!matchedTour) {
      return false;
    }
    return startTour(matchedTour.id, { force: true });
  }, [matchedTour, startTour]);

  return { canStart, start, tourId: matchedTour?.id ?? null };
}
